// Fully Responsive CounselorActivityReportPage.jsx with Bootstrap Mobile-First Design
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";
import { auditApi } from "../../utils/api";
import { toast } from "react-toastify";

const CounselorActivityReportPage = () => {
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    counselor_id: "",
    customer_id: "",
    authorization_status: "",
    last_service_period: "",
    authorization_search: "",
  });
  const [counselors, setCounselors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const { user, tenant_id } = useAuth();

  // Status options for the filter
  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "ACTIVE", label: "Active" },
    { value: "LOW", label: "Low (< 10 hours remaining)" },
    { value: "EXHAUSTED", label: "Exhausted" },
  ];

  // Last service period options
  const lastServiceOptions = [
    { value: "", label: "All Time" },
    { value: "no_service", label: "No Services Yet" },
    { value: "last_7_days", label: "Last 7 Days" },
    { value: "last_30_days", label: "Last 30 Days" },
    { value: "last_90_days", label: "Last 90 Days" },
    { value: "over_90_days", label: "Over 90 Days Ago" },
  ];

  // Load report data function
  const loadReport = useCallback(
    async (showToast = false) => {
      try {
        setLoading(true);

        const params = new URLSearchParams();
        if (filters.counselor_id)
          params.append("counselor_id", filters.counselor_id);
        if (filters.customer_id)
          params.append("customer_id", filters.customer_id);
        if (filters.authorization_status)
          params.append("authorization_status", filters.authorization_status);
        if (filters.last_service_period)
          params.append("last_service_period", filters.last_service_period);
        if (filters.authorization_search)
          params.append("authorization_search", filters.authorization_search);

        const response = await secureAxios.get(
          `/api/counselor-reports/activity?${params}`
        );

        if (response.data.success) {
          setReportData(response.data.data);
          setSummary(response.data.summary);

          if (showToast) {
            toast.success(
              `Report updated: ${response.data.summary.total_authorizations} authorizations found`
            );
          }
        } else {
          throw new Error("Failed to generate report");
        }
      } catch (err) {
        console.error("❌ Error loading counselor report:", err);
        if (showToast) {
          toast.error("Failed to load counselor activity report");
        }
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [filters]
  );

  // Load filter options on component mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        let counselorRes;
        try {
          counselorRes = await secureAxios.get(
            "/api/counselor-reports/counselors"
          );
        } catch (err) {
          console.log("Using fallback counselors endpoint");
          counselorRes = await secureAxios.get("/api/counselors");
        }

        console.log("Counselors loaded:", counselorRes.data);
        setCounselors(counselorRes.data);

        if (user?.is_admin || user?.tenant_type === "customer") {
          try {
            const customerRes = await secureAxios.get("/api/customers");
            setCustomers(customerRes.data);
          } catch (err) {
            console.log("Customer data not accessible:", err.message);
          }
        }
      } catch (err) {
        console.error("Error loading filter options:", err);
        toast.error("Failed to load filter options");
      }
    };

    if (tenant_id) {
      loadFilterOptions();
    }
  }, [tenant_id, user]);

  // Auto-load report when component mounts and when filters change
  useEffect(() => {
    if (tenant_id) {
      const timeoutId = setTimeout(() => {
        loadReport(false);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [tenant_id, loadReport]);

  // Export to CSV
  const exportToCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.counselor_id)
        params.append("counselor_id", filters.counselor_id);
      if (filters.customer_id)
        params.append("customer_id", filters.customer_id);
      if (filters.authorization_status)
        params.append("authorization_status", filters.authorization_status);
      if (filters.last_service_period)
        params.append("last_service_period", filters.last_service_period);
      if (filters.authorization_search)
        params.append("authorization_search", filters.authorization_search);

      const response = await secureAxios.get(
        `/api/counselor-reports/activity/export?${params}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const timestamp = new Date().toISOString().split("T")[0];
      link.download = `counselor_activity_report_${timestamp}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Report exported successfully!");
    } catch (err) {
      console.error("❌ Error exporting report:", err);
      toast.error("Failed to export report");
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      counselor_id: "",
      customer_id: "",
      authorization_status: "",
      last_service_period: "",
      authorization_search: "",
    });
  };

  const handleRefresh = () => {
    loadReport(true);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "EXHAUSTED":
        return "badge bg-danger";
      case "LOW":
        return "badge bg-warning";
      case "ACTIVE":
        return "badge bg-success";
      default:
        return "badge bg-secondary";
    }
  };

  return (
    <div
      className="container-fluid px-3 px-sm-4 px-lg-5"
      style={{ maxWidth: "1600px", margin: "0 auto" }}
    >
      {/* HIPAA Compliance Notice */}
      <div className="alert alert-info d-flex align-items-start mb-3">
        <i className="bi bi-shield-lock me-2 flex-shrink-0"></i>
        <div className="small">
          <strong>HIPAA Notice:</strong> This activity report contains Protected
          Health Information (PHI). All access is logged and monitored for
          compliance and audit purposes.
        </div>
      </div>

      {/* Header Section */}
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-3">
        <div className="mb-2 mb-lg-0">
          <h2 className="fs-3 fs-lg-2 mb-1">
            <i className="bi bi-chart-line me-2"></i>
            Counselor Activity Report
          </h2>
          <p className="text-muted mb-0">
            Track counselors, consumers, vendors, and open authorizations
            {loading && (
              <span className="ms-2">
                <i className="bi bi-arrow-clockwise me-1"></i> Updating...
              </span>
            )}
          </p>
        </div>
        <div className="d-flex flex-column flex-sm-row gap-2">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh Report"
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            <span className="d-none d-sm-inline">Refresh</span>
          </button>
          {reportData.length > 0 && (
            <button
              className="btn btn-success btn-sm"
              onClick={exportToCSV}
              disabled={loading}
            >
              <i className="bi bi-download me-1"></i>
              <span className="d-none d-sm-inline">Export </span>CSV
            </button>
          )}
        </div>
      </div>

      {/* Filters Card */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-semibold">
              <i className="bi bi-funnel me-2"></i>
              Filters
            </h6>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setFiltersOpen(!filtersOpen)}
              aria-expanded={filtersOpen}
              aria-controls="filters-collapse"
            >
              <i className={`bi bi-chevron-${filtersOpen ? "up" : "down"}`}></i>
            </button>
          </div>
        </div>
        <div
          className={`collapse ${filtersOpen ? "show" : ""}`}
          id="filters-collapse"
        >
          <div className="card-body">
            {/* First Row - Dropdowns */}
            <div className="row g-2 g-md-3 mb-3">
              {/* Counselor Filter */}
              <div className="col-12 col-sm-6 col-lg-3">
                <label className="form-label small fw-semibold">
                  Counselor:
                </label>
                <select
                  className="form-select form-select-sm"
                  value={filters.counselor_id}
                  onChange={(e) =>
                    handleFilterChange("counselor_id", e.target.value)
                  }
                  disabled={loading}
                >
                  <option value="">All Counselors</option>
                  {counselors.map((counselor) => (
                    <option
                      key={counselor.counselor_id}
                      value={counselor.counselor_id}
                    >
                      {counselor.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Customer Filter - Only show if data available */}
              {customers.length > 0 && (
                <div className="col-12 col-sm-6 col-lg-3">
                  <label className="form-label small fw-semibold">
                    Customer:
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.customer_id}
                    onChange={(e) =>
                      handleFilterChange("customer_id", e.target.value)
                    }
                    disabled={loading}
                  >
                    <option value="">All Customers</option>
                    {customers.map((customer) => (
                      <option
                        key={customer.customer_id}
                        value={customer.customer_id}
                      >
                        {customer.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Status Filter */}
              <div className="col-12 col-sm-6 col-lg-3">
                <label className="form-label small fw-semibold">Status:</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.authorization_status}
                  onChange={(e) =>
                    handleFilterChange("authorization_status", e.target.value)
                  }
                  disabled={loading}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Last Service Filter */}
              <div className="col-12 col-sm-6 col-lg-3">
                <label className="form-label small fw-semibold">
                  Last Service:
                </label>
                <select
                  className="form-select form-select-sm"
                  value={filters.last_service_period}
                  onChange={(e) =>
                    handleFilterChange("last_service_period", e.target.value)
                  }
                  disabled={loading}
                >
                  {lastServiceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Second Row - Search and Clear */}
            <div className="row g-2 g-md-3">
              <div className="col-12">
                <label className="form-label small fw-semibold">
                  Authorization Search:
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by authorization number..."
                  value={filters.authorization_search}
                  onChange={(e) =>
                    handleFilterChange("authorization_search", e.target.value)
                  }
                  disabled={loading}
                />
                <button
                  className="btn btn-outline-secondary btn-sm mt-2 w-100"
                  onClick={clearFilters}
                  disabled={loading}
                >
                  <i className="bi bi-x me-1"></i>Clear All
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading indicator for initial load */}
      {initialLoad && loading && (
        <div className="text-center py-4 py-md-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted small">
            Loading counselor activity report...
          </p>
        </div>
      )}

      {/* Summary Stats */}
      {!initialLoad && summary.total_authorizations > 0 && (
        <div className="row g-2 g-md-3 mb-4">
          <div className="col-6 col-lg-3">
            <div className="card h-100 border-info">
              <div className="card-body text-center py-3">
                <div className="text-info mb-2">
                  <i className="bi bi-people fs-2"></i>
                </div>
                <h6 className="card-subtitle mb-2 text-muted small">
                  Counselors
                </h6>
                <h4 className="card-title mb-0 fs-5 fs-md-4 text-info fw-bold">
                  {summary.unique_counselors}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="card h-100 border-primary">
              <div className="card-body text-center py-3">
                <div className="text-primary mb-2">
                  <i className="bi bi-person-circle fs-2"></i>
                </div>
                <h6 className="card-subtitle mb-2 text-muted small">
                  Consumers
                </h6>
                <h4 className="card-title mb-0 fs-5 fs-md-4 text-primary fw-bold">
                  {summary.unique_consumers}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="card h-100 border-success">
              <div className="card-body text-center py-3">
                <div className="text-success mb-2">
                  <i className="bi bi-building fs-2"></i>
                </div>
                <h6 className="card-subtitle mb-2 text-muted small">Vendors</h6>
                <h4 className="card-title mb-0 fs-5 fs-md-4 text-success fw-bold">
                  {summary.unique_vendors}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="card h-100 border-warning">
              <div className="card-body text-center py-3">
                <div className="text-warning mb-2">
                  <i className="bi bi-file-text fs-2"></i>
                </div>
                <h6 className="card-subtitle mb-2 text-muted small">
                  Authorizations
                </h6>
                <h4 className="card-title mb-0 fs-5 fs-md-4 text-warning fw-bold">
                  {summary.total_authorizations}
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Financial Summary */}
      {!initialLoad && summary.total_authorization_value && (
        <div className="row g-2 g-md-3 mb-4">
          <div className="col-12 col-md-4">
            <div className="card border-primary h-100">
              <div className="card-body text-center p-2 p-md-3">
                <h6 className="text-primary small mb-1">Total Value</h6>
                <h4 className="text-primary mb-0 fs-5 fs-md-4">
                  ${summary.total_authorization_value}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="card border-danger h-100">
              <div className="card-body text-center p-2 p-md-3">
                <h6 className="text-danger small mb-1">Amount Used</h6>
                <h4 className="text-danger mb-0 fs-5 fs-md-4">
                  ${summary.total_amount_used}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="card border-success h-100">
              <div className="card-body text-center p-2 p-md-3">
                <h6 className="text-success small mb-1">Remaining</h6>
                <h4 className="text-success mb-0 fs-5 fs-md-4">
                  ${summary.total_amount_remaining}
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Table View */}
      {!initialLoad && reportData.length > 0 && (
        <>
          {/* Desktop/Large Screen Table View */}
          <div className="d-none d-lg-block">
            <div className="table-responsive">
              <table className="table table-striped table-hover table-sm mb-0">
                <thead className="table-dark">
                  <tr>
                    <th className="text-nowrap small">Counselor</th>
                    <th className="text-nowrap small">Consumer</th>
                    <th className="text-nowrap small">Vendor</th>
                    <th className="text-nowrap small">Authorization</th>
                    <th className="text-nowrap small">Service</th>
                    <th className="text-nowrap small">Hours</th>
                    <th className="text-nowrap small">Amount</th>
                    <th className="text-nowrap small">Status</th>
                    <th className="text-nowrap small">Job Coach</th>
                    <th className="text-nowrap small">Last Service</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, index) => (
                    <tr key={index}>
                      <td className="py-2">
                        <div>
                          <strong className="small">
                            {row.counselor_name}
                          </strong>
                          <br />
                          <small className="text-muted">
                            {row.customer_name}
                          </small>
                        </div>
                      </td>
                      <td className="py-2">
                        <div>
                          <strong className="small">{row.consumer_name}</strong>
                          <br />
                          <small className="text-muted">
                            Case: {row.consumer_case_id}
                          </small>
                        </div>
                      </td>
                      <td className="py-2">
                        <div>
                          <strong className="small">{row.vendor_name}</strong>
                          <br />
                          <small className="text-muted">
                            {row.vendor_type}
                          </small>
                        </div>
                      </td>
                      <td className="py-2">
                        <div>
                          <strong className="small">
                            {row.authorization_number}
                          </strong>
                          <br />
                          <small className="text-muted">
                            {row.authorization_type}
                          </small>
                        </div>
                      </td>
                      <td className="py-2">
                        <div>
                          <strong className="small">{row.service_name}</strong>
                          <br />
                          <small className="text-muted">
                            ${row.service_rate}{" "}
                            {row.is_flat_rate === "Yes" ? "(Flat)" : "(Hourly)"}
                          </small>
                        </div>
                      </td>
                      <td className="py-2">
                        <div>
                          <strong className="small">
                            {row.hours_used} / {row.authorized_hours}
                          </strong>
                          <br />
                          <small className="text-muted">
                            {row.utilization_percentage} used
                          </small>
                        </div>
                      </td>
                      <td className="py-2">
                        <div>
                          <strong className="small">
                            ${row.amount_used} / $
                            {row.total_authorization_value}
                          </strong>
                          <br />
                          <small className="text-success">
                            ${row.amount_remaining} remaining
                          </small>
                        </div>
                      </td>
                      <td className="py-2">
                        <span
                          className={`${getStatusBadgeClass(
                            row.authorization_status
                          )} small`}
                        >
                          {row.authorization_status}
                        </span>
                      </td>
                      <td className="py-2">
                        <div>
                          <strong className="small">
                            {row.assigned_job_coach}
                          </strong>
                          <br />
                          <small className="text-muted">
                            {row.vendor_name}
                          </small>
                        </div>
                      </td>
                      <td className="py-2">
                        <div>
                          <small>{row.last_service_date}</small>
                          <br />
                          <small className="text-muted">
                            {row.service_count} services
                          </small>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tablet view - Card layout for medium screens */}
          <div className="d-none d-md-block d-lg-none">
            <div className="row g-3">
              {reportData.map((row, index) => (
                <div key={index} className="col-md-6">
                  <div className="card h-100">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="card-title mb-0 text-primary fw-bold">
                          {row.authorization_number}
                        </h6>
                        <span
                          className={`${getStatusBadgeClass(
                            row.authorization_status
                          )}`}
                        >
                          {row.authorization_status}
                        </span>
                      </div>
                      <p className="card-text text-muted mb-3">
                        {row.service_name}
                      </p>

                      <div className="row g-2 mb-3">
                        <div className="col-6">
                          <div className="text-center">
                            <div className="small text-muted">Counselor</div>
                            <div className="fw-bold small">
                              {row.counselor_name}
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="text-center">
                            <div className="small text-muted">Consumer</div>
                            <div className="fw-bold small">
                              {row.consumer_name}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row g-2 mb-3">
                        <div className="col-6">
                          <div className="text-center">
                            <div className="small text-muted">Vendor</div>
                            <div className="fw-bold small">
                              {row.vendor_name}
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="text-center">
                            <div className="small text-muted">Job Coach</div>
                            <div className="fw-bold small">
                              {row.assigned_job_coach}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3 p-2 bg-light rounded">
                        <div className="small text-muted">
                          Hours Used / Amount
                        </div>
                        <div className="fw-bold small">
                          {row.hours_used}/{row.authorized_hours} hrs
                        </div>
                        <div className="fw-bold small">
                          ${row.amount_used}/${row.total_authorization_value}
                        </div>
                        <div className="small text-success">
                          ${row.amount_remaining} remaining
                        </div>
                      </div>

                      <div className="mb-3 p-2 bg-light rounded">
                        <div className="small text-muted">Last Service</div>
                        <div className="fw-bold small">
                          {row.last_service_date}
                        </div>
                        <div className="small text-muted">
                          {row.service_count} services total
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile view - Stacked cards */}
          <div className="d-block d-md-none">
            <div className="row g-2">
              {reportData.map((row, index) => (
                <div key={index} className="col-12">
                  <div className="card">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-1 text-primary fw-bold fs-6">
                          {row.authorization_number}
                        </h6>
                        <span
                          className={`${getStatusBadgeClass(
                            row.authorization_status
                          )} fs-6`}
                        >
                          {row.authorization_status}
                        </span>
                      </div>

                      <p className="mb-2 text-muted small">
                        {row.service_name}
                      </p>

                      <div className="row g-2 text-center mb-3">
                        <div className="col-6">
                          <div className="bg-light p-2 rounded">
                            <div className="small text-muted">Counselor</div>
                            <div className="fw-bold small">
                              {row.counselor_name}
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-light p-2 rounded">
                            <div className="small text-muted">Consumer</div>
                            <div className="fw-bold small">
                              {row.consumer_name}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row g-2 text-center mb-3">
                        <div className="col-6">
                          <div className="bg-light p-2 rounded">
                            <div className="small text-muted">Vendor</div>
                            <div className="fw-bold small">
                              {row.vendor_name}
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-light p-2 rounded">
                            <div className="small text-muted">Job Coach</div>
                            <div className="fw-bold small">
                              {row.assigned_job_coach}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3 p-2 bg-light rounded text-center">
                        <div className="small text-muted">Hours & Amount</div>
                        <div className="fw-bold small">
                          {row.hours_used}/{row.authorized_hours} hours
                        </div>
                        <div className="fw-bold small">
                          ${row.amount_used}/${row.total_authorization_value}
                        </div>
                        <div className="small text-success">
                          ${row.amount_remaining} remaining
                        </div>
                      </div>

                      <div className="mb-3 p-2 bg-light rounded text-center">
                        <div className="small text-muted">Last Service</div>
                        <div className="fw-bold small">
                          {row.last_service_date}
                        </div>
                        <div className="small text-muted">
                          {row.service_count} services
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* No Data Message */}
      {!initialLoad &&
        !loading &&
        reportData.length === 0 &&
        summary.total_authorizations !== undefined && (
          <div className="text-center py-4 py-md-5">
            <i className="bi bi-chart-line fa-2x fa-md-3x text-muted mb-3"></i>
            <h5 className="text-muted fs-6 fs-md-5">
              No open authorizations found
            </h5>
            <p className="text-muted small">
              Try adjusting your filters or check if there are any active
              authorizations.
            </p>
          </div>
        )}
    </div>
  );
};

export default CounselorActivityReportPage;
